import { useMemo } from 'react';
import { useLicenseAccess } from './useLicenseAccess';

const mapIdentifierToDatasetId = (identifierFieldTypeKey: number): string => {
  switch (identifierFieldTypeKey) {
    case 1: return "0"; // Global/Subsurface
    case 2: return "1"; // Infrastructure Assets
    case 4: return "2"; // Emissions
    case 8: return "3"; // CCS Projects
    case 32: return "4"; // Economics/Blocks
    case 64: return "5"; // User datasets/Licences
    case 128: return "6"; // Other datasets/Windfarms
    default: return "0"; // Default to subsurface
  }
};

export const useTagAttributeFiltering = () => {
  const { checkDatasetAccess, licenseInfo } = useLicenseAccess();

  // Filter tag attributes based on user access
  const filterTagAttributesByAccess = useMemo(() => 
    (tagAttributes: any[], isProductionContext = false) => {
      return tagAttributes.filter(attr => {
        if (!attr.identifierFieldTypeKey) return true; // Allow if no identifier
        
        const datasetId = mapIdentifierToDatasetId(attr.identifierFieldTypeKey);
        const access = checkDatasetAccess(datasetId);
        
        // For production plots, allow attributes if user has subsurface license
        // even if they don't have infrastructure/production dataset access
        if (isProductionContext && !access.hasAccess) {
          const hasSubsurfaceLicense = licenseInfo?.datasets?.includes("subsurface_no_grid") || 
                                       licenseInfo?.datasets?.includes("subsurface_with_grid");
          if (hasSubsurfaceLicense) {
            return true; // Allow production attributes for subsurface users
          }
        }
        
        return access.hasAccess;
      });
    }, [checkDatasetAccess, licenseInfo]);

  // Filter datasets based on user access  
  const filterDatasetsByAccess = useMemo(() =>
    (datasets: any[]) => {
      return datasets.filter(dataset => {
        const datasetId = parseInt(dataset.id);
        if (isNaN(datasetId)) return true; // Keep non-numeric IDs
        
        // Map dataset IDs to license system dataset IDs (strings)
        let licenseDatasetId: string;
        switch (datasetId) {
          case 1: licenseDatasetId = "0"; break; // Subsurface
          case 2: licenseDatasetId = "1"; break; // Infrastructure Assets
          case 4: licenseDatasetId = "2"; break; // Emissions
          case 8: licenseDatasetId = "3"; break; // CCS Projects
          case 32: licenseDatasetId = "4"; break; // Economics/Blocks
          case 64: licenseDatasetId = "5"; break; // User datasets/Licences
          case 128: licenseDatasetId = "3"; break; // CCS related (Windfarms)
          case 239: return false; // Always exclude 239
          default: licenseDatasetId = "0"; break;
        }
        
        const access = checkDatasetAccess(licenseDatasetId);
        return access.hasAccess;
      });
    }, [checkDatasetAccess]);

  return {
    filterTagAttributesByAccess,
    filterDatasetsByAccess,
    checkDatasetAccess,
    mapIdentifierToDatasetId
  };
}; 